// Small shared render-states: a centered spinner, a typed error panel, an
// empty-state note, and skeleton table rows. Kept in one place so every page
// reports loading/failure the same way (doctrine 1.5 — errors surfaced, never
// swallowed).
import { Button, Skeleton, Spinner } from "@recruiter/shared";

import { ApiError } from "../lib/api";
import { AlertIcon, InboxIcon } from "./icons";

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="ad-state" role="status" aria-live="polite">
      <Spinner />
      <span className="ad-state__label">{label}</span>
    </div>
  );
}

export function EmptyState({
  title,
  message,
  action,
}: {
  title: string;
  message?: string;
  /** An optional way OUT of the empty state, for the cases where the reader can
   *  actually do something about it (retry a computation that is taking too
   *  long). Most empty states are just "nothing here yet" and pass nothing. */
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="ad-state">
      <span className="ad-state__icon" aria-hidden="true">
        <InboxIcon />
      </span>
      <p className="ad-state__title">{title}</p>
      {message ? <p className="ad-state__message">{message}</p> : null}
      {action ? (
        <Button variant="outline" size="sm" onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}

/** Turn an unknown query error into a readable line. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="ad-state" role="alert">
      <span className="ad-state__icon ad-state__icon--danger" aria-hidden="true">
        <AlertIcon />
      </span>
      <p className="ad-state__title">Couldn't load that</p>
      <p className="ad-state__message">{errorMessage(error)}</p>
      {onRetry ? (
        <Button variant="outline" tone="neutral" size="sm" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

/** Skeleton table rows in the real table geometry, so the loaded table doesn't
 *  jump. Widths vary deterministically (no random re-renders). */
export function TableSkeleton({ columns, rows = 6 }: { columns: number; rows?: number }) {
  return (
    <div
      className="rc-table-wrap rc-table-skeleton"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <table className="rc-table">
        <tbody>
          {Array.from({ length: rows }, (_, r) => (
            <tr key={r}>
              {Array.from({ length: columns }, (_, c) => (
                <td key={c}>
                  <Skeleton variant="text" width={`${45 + ((r * 7 + c * 13) % 40)}%`} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <span className="ad-visually-hidden">Loading…</span>
    </div>
  );
}
